import {
  FileFilterColumns,
  FileFilterModel,
  Media,
  MediaType,
  useFileFilter,
  useSetFileFilter,
  useValidateFileListOrder,
} from '@gripp/shared-logic';
import {
  Colors,
  colors,
  ColumnData,
  ColumnsFilterScreen,
  ColumnsFilterScreenHandle,
  SettingsIcon,
  tokens,
} from '@gripp/shared-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import EditCategory from '../../assets/components/editCategory';
import { FilePreviewHeaderActions } from '../../assets/components/filesFilePreviewPanel';
import { HeaderResetButton } from '../../shared/components/headerResetButton/headerResetButton';
import {
  buildPreviewMain,
  PreviewMediaResult,
  truncateTitle,
} from '../../shared/hooks/useFilePreview';
import { EditFileForm } from './editFileForm';

type CategoryItem = { id: string; name: string };

const FilePreviewHeaderRightActions = ({
  fileId,
  media,
}: {
  fileId: string;
  media: Media;
}) => {
  const navigate = useNavigate();
  const isImage = media.type === MediaType.Image;
  const isVideo = media.type === MediaType.Video;

  return (
    <View style={styles.previewHeaderRight} testID="files-file-preview-header">
      {!isImage && !isVideo && <FilePreviewHeaderActions media={media} />}
      <Pressable
        onPress={() => navigate(`/files/${fileId}/edit`)}
        style={styles.settingsButton}
        accessibilityRole="button"
        accessibilityLabel="Edit file"
      >
        <SettingsIcon size={24} color={Colors.role.textPrimary} />
      </Pressable>
    </View>
  );
};

export const useFilesFilterRightColumn = (
  active: boolean
): ColumnData | undefined => {
  const filterRef = useRef<ColumnsFilterScreenHandle>(null);
  const navigate = useNavigate();
  const { data: filter } = useFileFilter();
  const { mutateAsync: setFilter } = useSetFileFilter();
  const validateListOrder = useValidateFileListOrder();

  return useMemo(() => {
    if (!active) return undefined;
    return {
      title: 'file.list.files',
      mainComponent: (
        <ColumnsFilterScreen
          ref={filterRef}
          mode="page"
          filter={filter}
          setFilter={setFilter}
          validateListOrder={validateListOrder}
          createDefaultFilter={() => new FileFilterModel()}
          createFilter={(data) => new FileFilterModel(data)}
          baseColumns={FileFilterColumns}
          i18nPrefix="file.filter"
          viewResultsTranslationKey="common.apply"
          bottomBar={() => <></>}
        />
      ),
      headerComponent: (
        <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
      ),
      showBackButton: true,
      onBackButtonPress: () => navigate('/files'),
    };
  }, [active, filter, setFilter, validateListOrder, navigate]);
};

export const useFilesPreviewRightColumn = (
  fileId: string | null,
  preview: PreviewMediaResult
): ColumnData | undefined => {
  const navigate = useNavigate();
  const { selectedMedia, galleryImages } = preview;

  return useMemo(() => {
    if (!fileId) return undefined;

    const rightColumnHeader = selectedMedia ? (
      <FilePreviewHeaderRightActions fileId={fileId} media={selectedMedia} />
    ) : null;

    return {
      title: truncateTitle(selectedMedia?.name, 'File'),
      showBackButton: true,
      onBackButtonPress: () => navigate('/files'),
      disableScroll: true,
      headerComponent: rightColumnHeader,
      mainComponent: buildPreviewMain(selectedMedia, galleryImages),
    };
  }, [fileId, selectedMedia, galleryImages, navigate]);
};

export const useFilesPreviewLeftColumn = (
  fileId: string | null,
  active: boolean,
  preview: PreviewMediaResult
): ColumnData | undefined => {
  const { selectedMedia, galleryImages } = preview;

  return useMemo(() => {
    if (!active || !fileId) return undefined;

    return {
      title: truncateTitle(selectedMedia?.name, 'File'),
      showBackButton: false,
      disableScroll: true,
      mainComponent: buildPreviewMain(selectedMedia, galleryImages),
    };
  }, [active, fileId, selectedMedia, galleryImages]);
};

export const useFilesEditColumns = (
  fileId: string | null,
  active: boolean,
  preview: PreviewMediaResult
): { rightColumn?: ColumnData; thirdColumn?: ColumnData } => {
  const navigate = useNavigate();
  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<CategoryItem[]>(
    []
  );

  const { selectedMedia } = preview;

  useEffect(() => {
    if (selectedMedia?.categories) {
      setSelectedCategories(selectedMedia.categories as CategoryItem[]);
    }
  }, [selectedMedia?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close third panel automatically when leaving edit mode
  useEffect(() => {
    if (!active && isCategoryPanelOpen) {
      setIsCategoryPanelOpen(false);
    }
  }, [active, isCategoryPanelOpen]);

  return useMemo(() => {
    if (!active || !fileId) {
      return { rightColumn: undefined, thirdColumn: undefined };
    }

    const backToPreview = () => navigate(`/files/${fileId}`);

    const rightColumn: ColumnData = {
      title: 'topNav.edit',
      showBackButton: true,
      onBackButtonPress: backToPreview,
      disableScroll: true,
      mainComponent: (
        <EditFileForm
          fileId={fileId}
          media={selectedMedia ?? undefined}
          onComplete={backToPreview}
          onDeleteSuccess={() => navigate('/files')}
          onCategoryEdit={() => setIsCategoryPanelOpen((prev) => !prev)}
          externalCategories={selectedCategories}
          onCategoriesChange={setSelectedCategories}
        />
      ),
    };

    const thirdColumn: ColumnData | undefined = isCategoryPanelOpen
      ? {
          title: 'file.create.category',
          showBackButton: true,
          disableScroll: true,
          onBackButtonPress: () => setIsCategoryPanelOpen(false),
          mainComponent: (
            <EditCategory
              selectedCategories={selectedCategories}
              onCategoriesChange={setSelectedCategories}
            />
          ),
        }
      : undefined;

    return { rightColumn, thirdColumn };
  }, [
    active,
    fileId,
    selectedMedia,
    selectedCategories,
    isCategoryPanelOpen,
    navigate,
  ]);
};

const styles = StyleSheet.create({
  previewHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  settingsButton: {
    padding: tokens.spacing.sm,
    borderRadius: tokens.borderRadius.sm,
    cursor: 'pointer' as never,
  },
});
